<script setup lang="ts">
import { computed, inject, onMounted, ref, shallowRef, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import ColorBadge from "@/components/ui/ColorBadge.vue";
import SimpleButton from "@/components/ui/SimpleButton.vue";
import SkoreSpinner from "@/components/ui/SkoreSpinner.vue";
import TextInput from "@/components/ui/TextInput.vue";
import UserAvatar from "@/components/ui/UserAvatar.vue";
import { getUserFriendlyName, type UserDto, type WorkspaceDto } from "@/dto";
import { raiseErrorKey } from "@/injectionKeys";
import DefaultPageLayout from "@/layouts/DefaultPageLayout.vue";
import { RouteNames } from "@/router";
import { useApi } from "@/services/api";
import { formatDatetime } from "@/services/utils";
import { useAuthStore } from "@/stores/auth";
import { useModalsStore } from "@/stores/modals";
import { useToastsStore } from "@/stores/toasts";

const api = useApi();
const auth = useAuthStore();
const route = useRoute();
const router = useRouter();
const modals = useModalsStore();
const toasts = useToastsStore();

const raiseError = inject(raiseErrorKey);

const isLoading = ref(true);
const isLoadingMembers = ref(true);
const originalWorkspace = ref<WorkspaceDto | null>(null);
const workspaceName = ref("");
const workspacePublicId = ref("");
const members = shallowRef(new Map<string, UserDto>());
const needsSave = ref(false);
const isworkspacePublicIdAvailable = ref(true);
const suggestedworkspacePublicId = ref("");

const workspaceId = computed(() => parseInt(route.params.workspaceId as string));

const ownerId = computed(
  () => originalWorkspace.value?.members?.find((m) => m.role === "owner")?.user_id
);

async function fetchWorkspace() {
  isLoading.value = true;
  const t = await api.fetchWorkspace(workspaceId.value);
  if (t !== null) {
    originalWorkspace.value = structuredClone(t);
    workspaceName.value = t.name;
    workspacePublicId.value = t.public_id;
  } else {
    raiseError?.(404);
  }
  isLoading.value = false;
}

async function fetchMembers() {
  isLoadingMembers.value = true;
  if (originalWorkspace.value && originalWorkspace.value.members) {
    const idsToFetch = new Set<string>();
    originalWorkspace.value.members.forEach((m) => {
      idsToFetch.add(m.user_id);
      if (m.invited_by !== undefined && m.invited_by !== null) {
        idsToFetch.add(m.invited_by);
      }
    });
    const results = new Map<string, UserDto>();
    for (const id of idsToFetch) {
      const u = await api.fetchUser(id);
      if (u) {
        results.set(id, u);
      }
    }
    members.value = results;
  }
  isLoadingMembers.value = false;
}

function getMemberFriendlyName(member: UserDto) {
  return getUserFriendlyName(member);
}

function getInviterFriendlyName(userId: string) {
  const inviter = members.value.get(userId);
  return inviter ? getMemberFriendlyName(inviter) : "Deleted user";
}

async function onInviteMember() {
  const [isConfirmed, email] = await modals.prompt({
    title: "Invite member",
    titleIcon: "icon-new-user",
    message: "Enter the email of the member you want to invite.",
    promptedValueName: "email",
    inputType: "email",
    inputPattern: "^[^@\s]+@[^@\s]+\.[^@\s]+$",
  });
  if (isConfirmed) {
    await api.inviteWorkspaceMembers(workspaceId.value, [email]);
    toasts.addToast("Invitation sent", "success", { duration: 5 });
  }
}

async function onRemoveMember(member: UserDto) {
  const isConfirmed = await modals.confirm({
    title: "Remove member",
    titleIcon: "icon-trash",
    message: `Are you sure you want to remove ${getMemberFriendlyName(member)} from the workspace?`,
  });
  if (isConfirmed) {
    await api.removeWorkspaceMember(workspaceId.value, member.id);
    toasts.addToast("Member removed", "success", { duration: 5 });
    await fetchWorkspace();
    await fetchMembers();
  }
}

async function onSaveWorkspace() {
  if (originalWorkspace.value) {
    const success = await api.updateWorkspace(
      workspaceId.value,
      workspaceName.value,
      workspacePublicId.value
    );
    if (success) {
      toasts.addToast("Workspace updated", "success", { duration: 5 });
      await fetchWorkspace();
    }
  }
}

async function onDeleteWorkspace() {
  const isConfirmed = await modals.confirm({
    title: "Delete workspace",
    titleIcon: "icon-trash",
    message: "Are you sure you want to delete this workspace? This action cannot be undone.",
    isDangerous: true,
  });
  if (isConfirmed) {
    const success = await api.deleteWorkspace(workspaceId.value);
    if (success) {
      toasts.addToast("Workspace deleted", "success", { duration: 5 });
      router.push({ name: RouteNames.Dispatch });
    }
  }
}

async function checkworkspacePublicIdAvailability() {
  const { available, suggested_slug } = await api.checkworkspacePublicIdAvailability(
    workspacePublicId.value
  );
  isworkspacePublicIdAvailable.value = available;
  suggestedworkspacePublicId.value = suggested_slug ?? "";
}

watch([workspaceName, workspacePublicId], () => {
  needsSave.value =
    (workspaceName.value !== originalWorkspace.value?.name ||
      workspacePublicId.value !== originalWorkspace.value?.public_id) &&
    isworkspacePublicIdAvailable.value;
});

onMounted(async () => {
  await fetchWorkspace();
  await fetchMembers();
});
</script>

<template>
  <DefaultPageLayout
    :breadcrumb-items="[
      { label: 'Workspaces', to: { name: RouteNames.Dispatch } },
      { label: `${originalWorkspace?.name} settings` },
    ]"
  >
    <div class="workspace-detail-view">
      <Transition name="fade">
        <SkoreSpinner v-if="isLoading" />
        <div v-else-if="originalWorkspace !== null" class="wrapper">
          <header>
            <div>
              <h1 class="text-2xl-medium">
                <i class="icon icon-gear" />
                Workspace settings
              </h1>
            </div>

            <SimpleButton label="Save" :disabled="!needsSave" @click="onSaveWorkspace" />
          </header>

          <section>
            <header class="general-information">
              <div>
                <h2 class="text-lg-medium">General information</h2>
                <h3 class="text-base-regular">Manage general information about your workspace.</h3>
              </div>
            </header>

            <div class="card-content form">
              <TextInput
                label="Name"
                v-model="workspaceName"
                :disabled="auth.currentUser?.id !== ownerId"
              />
            </div>
          </section>

          <section>
            <header>
              <div>
                <h2 class="text-lg-medium">Members</h2>
                <h3 class="text-base-regular">Manage members inside your organisation.</h3>
              </div>

              <SimpleButton
                icon-left="icon-new-user"
                label="Invite member"
                kind="ghost"
                @click="onInviteMember"
              />
            </header>

            <SkoreSpinner v-if="isLoadingMembers" />
            <table v-else class="tabular-list members">
              <thead>
                <tr>
                  <th class="text-base-medium">User</th>
                  <th class="text-base-medium">Mail</th>
                  <th class="text-base-medium">Joined</th>
                  <th class="text-base-medium">Invited by</th>
                  <th class="text-base-medium">Role</th>
                  <th class="text-base-medium">Actions</th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="member in originalWorkspace.members" :key="member.user_id">
                  <td class="text-base-regular">
                    <UserAvatar :name="getMemberFriendlyName(members.get(member.user_id)!)" />
                  </td>
                  <td class="text-base-regular">{{ members.get(member.user_id)!.email }}</td>
                  <td class="text-base-regular">
                    {{ formatDatetime(originalWorkspace.created_at) }}
                  </td>
                  <td class="text-base-regular">
                    {{ getInviterFriendlyName(member.invited_by ?? "") }}
                  </td>
                  <td class="text-base-regular">{{ member.role }}</td>
                  <td class="text-base-regular">
                    <SimpleButton
                      class="remove-member"
                      icon-right="icon-trash"
                      label="Remove"
                      :disabled="member.role === 'owner' || auth.currentUser?.id !== ownerId"
                      @click="onRemoveMember(members.get(member.user_id)!)"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </section>

          <section>
            <header class="general-information">
              <div>
                <h2 class="text-lg-medium">Danger zone</h2>
                <h3 class="text-base-regular">Dangerous actions that can't be undone.</h3>
              </div>
            </header>
            <div class="card-content danger-zone form">
              <div class="form-group">
                <TextInput
                  label="Public ID"
                  v-model="workspacePublicId"
                  :disabled="auth.currentUser?.id !== ownerId"
                  @keyup="checkworkspacePublicIdAvailability"
                />

                <Transition name="fade">
                  <div
                    v-if="!isworkspacePublicIdAvailable && suggestedworkspacePublicId"
                    class="error"
                  >
                    This workspace name is not unique, try something else. (like
                    <a
                      href="#"
                      @click="
                        workspacePublicId = suggestedworkspacePublicId;
                        isworkspacePublicIdAvailable = true;
                      "
                    >
                      {{ suggestedworkspacePublicId }}</a
                    >)
                  </div>
                </Transition>

                <ColorBadge color="red" left-icon="icon-warning">
                  Changing the public ID will break existing code that uses the old ID.
                </ColorBadge>
              </div>

              <SimpleButton
                label="Delete workspace"
                kind="ghost"
                @click="onDeleteWorkspace"
                :disabled="auth.currentUser?.id !== ownerId"
              />
            </div>
          </section>
        </div>
      </Transition>
    </div>
  </DefaultPageLayout>
</template>

<style scoped>
.workspace-detail-view {
  .back {
    color: var(--content-secondary);
    text-decoration: none;

    i {
      padding-right: var(--numbers-8);
      vertical-align: sub;
    }
  }

  .wrapper {
    display: flex;
    flex-direction: column;
    padding: calc(var(--numbers-32) - var(--numbers-12)) 0 var(--numbers-32) 0;
    gap: var(--numbers-32);

    section {
      display: flex;
      flex-direction: column;
      gap: var(--numbers-16);
    }

    header {
      display: flex;
      align-items: center;
      justify-content: space-between;

      h1,
      h2 {
        color: var(--content-primary);
      }

      h3 {
        color: var(--content-tertiary);
      }

      h1 {
        display: flex;
        align-items: center;
        color: var(--content-primary);
        gap: var(--numbers-8);

        i {
          margin-right: var(--numbers-6);
          color: var(--content-tertiary);
        }
      }

      .icon {
        display: inline-flex;
        width: var(--numbers-32);
        height: var(--numbers-32);
        align-items: center;
        justify-content: center;
        border-radius: var(--numbers-6);
        background-color: var(--background-selected);
        color: var(--content-brand);
      }
    }

    .form {
      display: flex;
      flex-direction: column;
      gap: var(--numbers-24);

      .form-group {
        display: flex;
        flex-direction: column;
        gap: var(--numbers-8);
      }
    }

    .danger-zone {
      border-color: var(--border-negative);

      .error {
        color: var(--content-negative);

        a,
        a:visited {
          color: inherit;
        }
      }

      .skore-button.ghost {
        width: fit-content;
        border-color: var(--content-negative);
        background-color: var(--background-negative-subtle);
        color: var(--content-negative-bold);

        &:disabled {
          border-color: var(--content-disabled);
          background-color: var(--background-disabled);
          color: var(--content-disabled);
        }
      }
    }

    table.members {
      td {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    .remove-member:not(:disabled) {
      background-color: var(--content-negative);
      color: var(--content-primary-inverse);
    }
  }
}
</style>
